// client/src/pages/PublicInvoiceViewPage.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicInvoice } from '../api/invoices';
import { motion } from 'framer-motion';
import { FiCalendar, FiMapPin, FiMail, FiCheckCircle, FiAlertCircle, FiClock, FiDollarSign, FiCreditCard } from 'react-icons/fi';
import 'react-quill-new/dist/quill.snow.css';

const PublicInvoiceViewPage = () => {
    const { publicAccessToken } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadInvoice = async () => {
            try {
                setLoading(true);
                const { invoiceData } = await fetchPublicInvoice(publicAccessToken);

                console.log('Full invoice data:', invoiceData);

                setInvoice(invoiceData);
            } catch (err) {
                setError(err.message || 'Could not retrieve invoice. The link may be invalid or expired.');
            } finally {
                setLoading(false);
            }
        };
        loadInvoice();
    }, [publicAccessToken]);

    const formatCurrency = (amount) => {
        const value = parseFloat(amount);
        if (isNaN(value)) return '$0.00';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid': return 'bg-green-100 text-green-800 border-green-200';
            case 'overdue': return 'bg-red-100 text-red-800 border-red-200';
            case 'sent': return 'bg-blue-100 text-blue-800 border-blue-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'paid': return 'PAID';
            case 'overdue': return 'OVERDUE';
            case 'sent': return 'SENT';
            case 'draft': return 'DRAFT';
            default: return status.toUpperCase();
        }
    };

    // Helper function to get terms and conditions from various possible locations
    const getTermsAndConditions = () => {
        return invoice.notes ||
            invoice.userCompany?.defaultTermsConditions ||
            null;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md mx-4">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                        <FiAlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Invoice</h3>
                    <p className="text-gray-500">{error}</p>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return null;
    }

    // Calculate summary totals
    const subtotal = invoice.sections?.reduce((acc, section) => {
        return acc + (section.items?.reduce((itemAcc, item) => {
            return itemAcc + (parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0));
        }, 0) || 0);
    }, 0) || parseFloat(invoice.subtotal || 0);

    const taxAmount = subtotal * parseFloat(invoice.taxRate || 0) / 100;
    const total = parseFloat(invoice.total || 0);

    // Get terms and conditions using helper function
    const termsAndConditions = getTermsAndConditions();

    // Check if invoice is overdue
    const dueDate = new Date(invoice.dueDate);
    const today = new Date();
    const isOverdue = dueDate < today && invoice.status !== 'paid';

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 }
    };

    return (
        <div className="min-h-screen font-sans py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
            {/* Background decoration */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-blue-100 blur-3xl opacity-50"></div>
                <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-indigo-100 blur-3xl opacity-50"></div>
            </div>

            <motion.div
                className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-sm shadow-2xl rounded-2xl overflow-hidden border border-white/50"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Gradient Top Bar */}
                <div className="h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

                {/* --- Header Section --- */}
                <header className="p-8 md:p-12 border-b border-gray-100">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-6 mb-6">
                                {invoice.userCompany?.companyLogoUrl && (
                                    <img
                                        src={invoice.userCompany.companyLogoUrl}
                                        alt="Company Logo"
                                        className="h-20 w-auto object-contain rounded-lg"
                                    />
                                )}
                                <div>
                                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">INVOICE</h1>
                                    <p className="text-blue-600 font-medium mt-1">#{invoice.invoiceNumber}</p>
                                </div>
                            </div>

                            {invoice.userCompany && (
                                <div className="space-y-1 text-gray-600">
                                    <h2 className="text-xl font-bold text-gray-900 mb-2">{invoice.userCompany.companyName}</h2>
                                    <div className="flex items-center gap-2">
                                        <FiMail className="w-4 h-4 text-gray-400" />
                                        <span>{invoice.userCompany.email}</span>
                                    </div>
                                    {invoice.userCompany.phone && (
                                        <div className="flex items-center gap-2">
                                            <FiCalendar className="w-4 h-4 text-gray-400" />
                                            <span>{invoice.userCompany.phone}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col items-end text-right">
                            <div className={`inline-flex items-center px-4 py-2 rounded-full border font-bold text-sm mb-6 shadow-sm ${getStatusColor(invoice.status)}`}>
                                {invoice.status === 'paid' && <FiCheckCircle className="mr-2" />}
                                {invoice.status === 'overdue' && <FiAlertCircle className="mr-2" />}
                                {getStatusText(invoice.status)}
                            </div>

                            <div className="space-y-2 text-right">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Amount Due</p>
                                    <p className="text-3xl font-bold text-gray-900">
                                        {formatCurrency(total - (invoice.paymentRecords?.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || 0))}
                                    </p>
                                </div>
                                <div className="pt-2">
                                    <p className="text-sm text-gray-500">Due Date</p>
                                    <p className={`font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                                        {formatDate(invoice.dueDate)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-gray-100">
                        <motion.div variants={itemVariants} className="bg-gray-50/50 p-6 rounded-xl border border-gray-100">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                                <FiMapPin className="text-blue-500" /> Bill To
                            </h3>
                            <div className="space-y-1 text-gray-600">
                                <p className="font-bold text-lg text-gray-900">{invoice.clientName}</p>
                                <p>{invoice.clientEmail}</p>
                                <p className="whitespace-pre-line">{invoice.clientAddress}</p>
                            </div>
                        </motion.div>

                        <motion.div variants={itemVariants} className="bg-gray-50/50 p-6 rounded-xl border border-gray-100">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                                <FiClock className="text-blue-500" /> Details
                            </h3>
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Invoice Date</span>
                                    <span className="font-medium text-gray-900">{formatDate(invoice.createdAt)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Due Date</span>
                                    <span className={`font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                                        {formatDate(invoice.dueDate)}
                                    </span>
                                </div>
                                {isOverdue && (
                                    <div className="flex justify-between text-red-600 bg-red-50 px-3 py-1 rounded-lg">
                                        <span className="font-medium">Days Overdue</span>
                                        <span className="font-bold">{Math.floor((today - dueDate) / (1000 * 60 * 60 * 24))} days</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </header>

                {/* --- Invoice Description --- */}
                {invoice.description && (
                    <motion.div variants={itemVariants} className="px-8 md:px-12 py-8 border-b border-gray-100">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Project Description</h3>
                        <div
                            className="prose prose-blue max-w-none text-gray-600 bg-gray-50 p-6 rounded-xl border border-gray-100"
                            dangerouslySetInnerHTML={{ __html: formatDescription(invoice.description) }}
                        />
                    </motion.div>
                )}

                {/* --- Sections --- */}
                <main className="px-8 md:px-12 py-8 bg-gray-50/30">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">Invoice Items</h3>
                    {invoice.sections && invoice.sections.length > 0 ? (
                        <div className="space-y-6">
                            {invoice.sections.map((section, index) => (
                                <motion.div
                                    variants={itemVariants}
                                    key={section.id || index}
                                    className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                                >
                                    <div className="p-6">
                                        <div className="flex justify-between items-start mb-4 pb-4 border-b border-gray-100">
                                            <h4 className="text-lg font-bold text-gray-900">{section.name}</h4>
                                            <span className="text-lg font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                                                {formatCurrency(section.sectionSubtotal || section.items?.reduce((acc, item) => acc + (parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0)), 0) || 0)}
                                            </span>
                                        </div>

                                        {/* Line Items Table */}
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left text-sm">
                                                <thead>
                                                    <tr className="text-gray-500 border-b border-gray-100">
                                                        <th className="pb-2 font-medium w-1/2">Item</th>
                                                        <th className="pb-2 font-medium text-center">Qty</th>
                                                        <th className="pb-2 font-medium text-right">Price</th>
                                                        <th className="pb-2 font-medium text-right">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50">
                                                    {section.items?.map((item, i) => (
                                                        <tr key={i} className="group">
                                                            <td className="py-3 pr-4">
                                                                <p className="font-medium text-gray-900">{item.name}</p>
                                                                {item.description && (
                                                                    <p className="text-gray-500 text-xs mt-1">{item.description}</p>
                                                                )}
                                                            </td>
                                                            <td className="py-3 text-center text-gray-600">{item.quantity}</td>
                                                            <td className="py-3 text-right text-gray-600">{formatCurrency(item.unitPrice)}</td>
                                                            <td className="py-3 text-right font-medium text-gray-900">
                                                                {formatCurrency(parseFloat(item.unitPrice || 0) * parseFloat(item.quantity || 0))}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {section.description && (
                                            <div
                                                className="mt-4 pt-4 border-t border-gray-100 prose prose-sm max-w-none text-gray-500"
                                                dangerouslySetInnerHTML={{ __html: formatDescription(section.description) }}
                                            />
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                            <FiAlertCircle className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                            <p className="text-gray-500">No items available for this invoice.</p>
                        </div>
                    )}
                </main>

                {/* --- Totals Footer --- */}
                <footer className="bg-gray-50 border-t border-gray-200">
                    <div className="px-8 md:px-12 py-8">
                        <div className="flex flex-col md:flex-row gap-12">
                            {/* Left Side: Notes & Payment Info */}
                            <div className="flex-1 space-y-8">
                                {termsAndConditions && (
                                    <motion.div variants={itemVariants}>
                                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                            <FiAlertCircle className="text-blue-500" /> Notes & Terms
                                        </h4>
                                        <div className="text-sm text-gray-600 bg-white p-4 rounded-xl border border-gray-200 shadow-sm whitespace-pre-wrap">
                                            {termsAndConditions}
                                        </div>
                                    </motion.div>
                                )}

                                <motion.div variants={itemVariants} className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                                    <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                                        <FiCreditCard className="text-blue-600" /> Payment Instructions
                                    </h4>
                                    <p className="text-blue-800 text-sm">
                                        Please contact <span className="font-bold">{invoice.userCompany?.companyName}</span> at{' '}
                                        <a href={`mailto:${invoice.userCompany?.email}`} className="underline hover:text-blue-600">
                                            {invoice.userCompany?.email}
                                        </a>{' '}
                                        for payment details.
                                    </p>
                                </motion.div>
                            </div>

                            {/* Right Side: Totals */}
                            <div className="w-full md:w-96">
                                <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                                    <div className="space-y-3">
                                        <div className="flex justify-between text-gray-600">
                                            <span>Subtotal</span>
                                            <span className="font-medium text-gray-900">{formatCurrency(subtotal)}</span>
                                        </div>
                                        <div className="flex justify-between text-gray-600">
                                            <span>Tax ({(parseFloat(invoice.taxRate || 0)).toFixed(2)}%)</span>
                                            <span className="font-medium text-gray-900">{formatCurrency(taxAmount)}</span>
                                        </div>
                                        <div className="h-px bg-gray-100 my-2"></div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-lg font-bold text-gray-900">Total</span>
                                            <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                                                {formatCurrency(total)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-6 space-y-3 pt-6 border-t border-gray-100">
                                        {(() => {
                                            const amountPaid = invoice.paymentRecords?.reduce((sum, payment) =>
                                                sum + parseFloat(payment.amount || 0), 0) || 0;
                                            const amountDue = total - amountPaid;

                                            return (
                                                <>
                                                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-100">
                                                        <span className="text-green-800 font-medium">Amount Paid</span>
                                                        <span className="text-green-700 font-bold">{formatCurrency(amountPaid)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                        <span className="text-gray-700 font-medium">Amount Due</span>
                                                        <span className="text-gray-900 font-bold">{formatCurrency(amountDue)}</span>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Payment History & Schedule */}
                    <div className="px-8 md:px-12 py-8 bg-gray-50/50 border-t border-gray-200">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Payment History */}
                            <motion.div variants={itemVariants}>
                                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <FiCheckCircle className="text-green-500" /> Payment History
                                </h4>
                                {invoice.paymentRecords && invoice.paymentRecords.length > 0 ? (
                                    <div className="space-y-3">
                                        {invoice.paymentRecords.map((payment, index) => (
                                            <div key={payment.id || index} className="flex justify-between items-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-gray-900">Payment #{index + 1}</span>
                                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">PAID</span>
                                                    </div>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {formatDate(payment.date_paid)} • {payment.payment_method || 'Unknown Method'}
                                                    </p>
                                                </div>
                                                <span className="font-bold text-gray-900">{formatCurrency(payment.amount)}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 bg-white rounded-xl border border-dashed border-gray-300">
                                        <p className="text-gray-500 text-sm">No payments recorded yet</p>
                                    </div>
                                )}
                            </motion.div>

                            {/* Payment Schedule */}
                            <motion.div variants={itemVariants}>
                                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <FiCalendar className="text-blue-500" /> Payment Schedule
                                </h4>
                                {invoice.payments && invoice.payments.length > 0 ? (
                                    <div className="space-y-3">
                                        {invoice.payments.map((payment, index) => {
                                            const amountPaid = parseFloat(payment.amount_paid || 0);
                                            const totalAmount = parseFloat(payment.amount || 0);
                                            const dueDate = new Date(payment.due_date);
                                            const today = new Date();
                                            let isPaid = amountPaid >= totalAmount && totalAmount > 0;
                                            let isOverdue = !isPaid && dueDate < today;

                                            return (
                                                <div key={payment.id || index} className={`p-4 rounded-xl border shadow-sm transition-all ${isPaid ? 'bg-green-50/50 border-green-100' :
                                                    isOverdue ? 'bg-red-50/50 border-red-100' :
                                                        'bg-white border-gray-200'
                                                    }`}>
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-bold text-gray-900">Payment #{payment.payment_number}</span>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isPaid ? 'bg-green-100 text-green-700' :
                                                                isOverdue ? 'bg-red-100 text-red-700' :
                                                                    'bg-blue-100 text-blue-700'
                                                                }`}>
                                                                {isPaid ? 'PAID' : isOverdue ? 'OVERDUE' : 'PENDING'}
                                                            </span>
                                                        </div>
                                                        <span className="font-bold text-gray-900">{formatCurrency(payment.amount)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-gray-500">{payment.description}</span>
                                                        <span className={`${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                                            Due: {formatDate(payment.due_date)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 bg-white rounded-xl border border-dashed border-gray-300">
                                        <p className="text-gray-500 text-sm">Full payment due by {formatDate(invoice.dueDate)}</p>
                                    </div>
                                )}
                            </motion.div>
                        </div>
                    </div>

                    <div className="text-center py-8 bg-gray-50">
                        <p className="text-sm text-gray-400 font-medium">Powered by <a href="/" className="text-gray-600 font-bold hover:text-blue-600 transition-colors">SwiftBidPro</a></p>
                    </div>
                </footer>
            </motion.div>
        </div>
    );
};

const formatDescription = (description) => {
    if (!description) return '';

    // Apply styling to unordered lists - let CSS handle nested levels
    let formatted = description.replace(/<ul[^>]*>/g, '<ul>');

    // Apply styling to ordered lists - let CSS handle nested levels
    formatted = formatted.replace(/<ol[^>]*>/g, '<ol>');

    // Remove inline styles from list items that might override our CSS
    formatted = formatted.replace(/<li[^>]*style="[^"]*"[^>]*>/g, (match) => {
        return match.replace(/style="[^"]*"/g, '');
    });

    // Clean up any Quill-specific classes that might interfere
    formatted = formatted.replace(/class="ql-indent-\d+"/g, '');

    return formatted;
};

export default PublicInvoiceViewPage;